import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User as UserIcon, ShoppingBag } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
export function SiteNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    itemCount,
    openCart
  } = useCart();
  const handleCartClick = () => {
    if (location.pathname === '/shop' || location.pathname === '/books') {
      openCart();
    } else {
      navigate('/account?tab=cart');
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Training',
    path: '/training'
  }, {
    name: 'Shop',
    path: '/shop'
  }, {
    name: 'Books',
    path: '/books'
  }];
  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#3B4A4F] ${isScrolled ? 'py-3 shadow-sm border-b border-white/10' : 'py-5 border-b border-transparent'}`} data-id="element-3449">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6" data-id="element-3450">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" aria-label="Home" data-id="element-3451">
          <img src="https://cdn.magicpatterns.com/uploads/4gduygJNEAxYz3SyjoFAYx/ChatGPT_Image_May_13,_2026,_09_47_03_PM.png" alt="Structured Nutrition" className="w-16 h-16 object-contain transition-transform group-hover:scale-105" data-id="element-3452" />
        </Link>

        {/* Desktop Nav (centered) */}
        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center" data-id="element-3453">
          {navLinks.map(link => {
          const active = isActive(link.path);
          return <Link key={link.name} to={link.path} className="relative text-[11px] font-bold uppercase tracking-[0.2em] transition-colors py-2" data-id="element-3454">
                <span className={active ? 'text-white' : 'text-white hover:text-[#C8C4B0] transition-colors'} data-id="element-3455">
                  {link.name}
                </span>
                {active && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-white" data-id="element-3456" />}
              </Link>;
        })}
          <Link to="/contact" className="relative text-[11px] font-bold uppercase tracking-[0.2em] transition-colors py-2" data-id="element-3457">
            <span className={isActive('/contact') ? 'text-white' : 'text-white hover:text-[#C8C4B0] transition-colors'} data-id="element-3458">
              Contact
            </span>
            {isActive('/contact') && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-white" data-id="element-3459" />}
          </Link>
        </nav>

        {/* Right side actions */}
        <div className="hidden lg:flex items-center gap-3 shrink-0" data-id="element-3460">
          <Link to={user ? '/account?tab=profile' : '/login'} className="inline-flex items-center justify-center w-9 h-9 text-white hover:text-[#C8C4B0] transition-colors" aria-label={user ? 'Account' : 'Sign in'} data-id="element-3461">
            <UserIcon className="w-[19px] h-[19px]" data-id="element-3462" />
          </Link>
          <button onClick={handleCartClick} className="relative inline-flex items-center justify-center w-9 h-9 text-white hover:text-[#C8C4B0] transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3463">
            <ShoppingBag className="w-[19px] h-[19px]" data-id="element-3464" />
            {itemCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-white text-[#3B4A4F] text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-id="element-3465">
                {itemCount}
              </span>}
          </button>
          <Link to="/contact" className="ml-2 inline-flex items-center px-5 py-3 border border-white text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-[#3B4A4F] transition-colors" data-id="element-3466">
            Book a Consultation
          </Link>
        </div>

        {/* Mobile actions */}
        <div className="lg:hidden flex items-center gap-1" data-id="element-3467">
          <button onClick={handleCartClick} className="relative inline-flex items-center justify-center w-10 h-10 text-white" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3468">
            <ShoppingBag className="w-5 h-5" data-id="element-3469" />
            {itemCount > 0 && <span className="absolute top-0.5 right-0.5 bg-white text-[#3B4A4F] text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-id="element-3470">
                {itemCount}
              </span>}
          </button>
          <button className="text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-3471">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-3472" /> : <Menu className="w-6 h-6" data-id="element-3473" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-[#3B4A4F] border-t border-white/10 shadow-lg" data-id="element-3474">
          <nav className="flex flex-col py-2" data-id="element-3475">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`px-6 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-colors text-white ${isActive(link.path) ? 'bg-white/10' : 'hover:bg-white/5'}`} data-id="element-3476">
                {link.name}
              </Link>)}
            <Link to="/contact" className={`px-6 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-colors text-white ${isActive('/contact') ? 'bg-white/10' : 'hover:bg-white/5'}`} data-id="element-3477">
              Contact
            </Link>
            <Link to={user ? '/account?tab=profile' : '/login'} className="px-6 py-4 text-sm font-bold text-white uppercase tracking-[0.2em] hover:bg-white/5 transition-colors border-t border-white/10 flex items-center gap-2" data-id="element-3478">
              <UserIcon className="w-4 h-4" data-id="element-3479" />
              {user ? 'Account' : 'Sign In'}
            </Link>
            <div className="px-6 py-4 border-t border-white/10" data-id="element-3480">
              <Link to="/contact" className="block w-full text-center px-5 py-3 border border-white text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-[#3B4A4F] transition-colors" data-id="element-3481">
                Book a Consultation
              </Link>
            </div>
          </nav>
        </div>}
    </header>;
}